import { type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { BOOKING_TIMEZONE, type ConsultationService } from '../../../types';
import { type BookingFormState } from '../../../hooks/useBookingAvailability';
import { Input } from '../../common/Input/Input';
import { Button } from '../../common/Button/Button';

interface BookingStepDetailsProps {
  service: ConsultationService;
  form: BookingFormState;
  submitting: boolean;
  submitError: string;
  handleChange: (field: keyof BookingFormState) => (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  onBack: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

export function BookingStepDetails({
  service,
  form,
  submitting,
  submitError,
  handleChange,
  onBack,
  onSubmit,
}: BookingStepDetailsProps) {
  const { t } = useTranslation();

  return (
    <form className="booking-form" onSubmit={onSubmit}>
      <div className="booking-card booking-card-summary">
        <div>
          <p className="booking-card-hint">{t('booking.summaryLabel')}</p>
          <h3 className="booking-card-title">{service.title}</h3>
        </div>
        <div className="booking-summary-meta">
          <span>
            <strong>{form.selectedDate}</strong>
          </span>
          <span>
            {form.selectedSlot?.label} ({BOOKING_TIMEZONE})
          </span>
          <button
            type="button"
            className="booking-link-btn"
            onClick={onBack}
          >
            {t('booking.changeTime')}
          </button>
        </div>
      </div>

      {submitError && <p className="booking-form-error">{submitError}</p>}

      <div className="booking-form-grid">
        <Input
          id="booking-name"
          label={t('booking.form.name')}
          value={form.customerName}
          onChange={handleChange('customerName')}
          required
          autoComplete="name"
        />

        <Input
          id="booking-email"
          type="email"
          label={t('booking.form.email')}
          value={form.email}
          onChange={handleChange('email')}
          required
          autoComplete="email"
        />

        <Input
          id="booking-phone"
          label={t('booking.form.phone')}
          value={form.phone}
          onChange={handleChange('phone')}
          required
          autoComplete="tel"
        />

        <Input
          id="booking-company"
          label={t('booking.form.company')}
          value={form.company}
          onChange={handleChange('company')}
          autoComplete="organization"
        />

        <Input
          id="booking-provider"
          type="select"
          label={t('booking.preferredCalendar')}
          value={form.provider}
          onChange={handleChange('provider')}
          options={[
            { value: 'GOOGLE', label: t('booking.providers.google') },
            { value: 'OUTLOOK', label: t('booking.providers.outlook') },
            { value: 'ICS', label: t('booking.providers.ics') },
          ]}
        />
      </div>

      <Input
        id="booking-notes"
        type="textarea"
        label={t('booking.form.notes')}
        rows={4}
        value={form.notes}
        onChange={handleChange('notes')}
      />

      <div className="booking-inline-meta">
        <span>{t('booking.durationValue', { minutes: service.durationMinutes })}</span>
        <span>{t('consultation.available')}</span>
      </div>

      <p className="booking-pending-note">{t('booking.pendingNote')}</p>

      <div className="booking-modal-actions">
        <Button
          variant="outline"
          onClick={onBack}
        >
          {t('booking.back')}
        </Button>
        <Button type="submit" loading={submitting}>
          {t('booking.form.submit')}
        </Button>
      </div>
    </form>
  );
}
